import cls from "@fvilers/cls";
import {
  ComponentPropsWithoutRef,
  use,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useRelativePosition } from "../../hooks/useRelativePosition";
import { Align, Side } from "../../hooks/useRelativePosition/types";
import { TooltipContext } from "./contexts/TooltipContext";
import styles from "./Tooltip.module.css";

type TooltipContentProps = {
  align?: Align;
  alignOffset?: number;
  arrowSize?: number;
  children: React.ReactNode;
  offset?: number;
  side?: Side;
  variant?: "error" | "info" | "warning";
  viewportMargin?: number;
} & ComponentPropsWithoutRef<"div">;

export function TooltipContent({
  align = "center",
  alignOffset = 0,
  children,
  className,
  offset = 8,
  side = "top",
  style,
  variant,
  viewportMargin = 8,
  ...props
}: TooltipContentProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const { open, setOpen, tooltipId, triggerRef } = use(TooltipContext);
  const [localOpen, setLocalOpen] = useState(open);

  const closeTooltip = useCallback(() => {
    setOpen(false);
    setLocalOpen(false);
  }, [setOpen]);

  const { position } = useRelativePosition({
    align,
    alignOffset,
    anchorRef: triggerRef,
    close: closeTooltip,
    contentRef,
    offset,
    open,
    side,
    viewportMargin,
  });

  useEffect(() => {
    if (open) {
      contentRef.current?.showPopover();
    }
  }, [open]);

  if (open && !localOpen) setLocalOpen(true);
  if (!open && localOpen) setTimeout(() => setLocalOpen(false), 100);

  if (!localOpen) return null;

  const getAnimationTransform = () => {
    if (!position) return undefined;
    switch (position.side) {
      case "bottom":
        return `translateY(-${offset}px)`;
      case "left":
        return `translateX(${offset}px)`;
      case "right":
        return `translateX(-${offset}px)`;
      case "top":
        return `translateY(${offset}px)`;
    }
  };

  return (
    <div
      className={cls(
        styles.TooltipContent,
        variant && styles[variant],
        className,
      )}
      data-side={position?.side}
      data-state={open ? "open" : "closed"}
      id={tooltipId}
      popover="manual"
      ref={contentRef}
      role="tooltip"
      style={
        {
          ...(style ?? {}),
          ...position,
          "--animation-transform": getAnimationTransform(),
        } as React.CSSProperties
      }
      tabIndex={-1}
      {...props}
    >
      {children}
    </div>
  );
}
